var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { iconButton } from './shared';
import './vitesse-icon';
let VitesseDropdown = class VitesseDropdown extends LitElement {
    constructor() {
        super(...arguments);
        this.triggerIcon = 'menu';
        /** Optional TEXT trigger — renders the current value as a pill instead of a bare
         *  icon button. For a list where every row has a value, an icon-only trigger
         *  hides the one thing the list exists to show: you would have to open each menu
         *  to read the state it already knows. Absent keeps the icon-only trigger, so
         *  every existing caller is unchanged. */
        this.triggerLabel = '';
        this.triggerTitle = '';
        /** A status dot after the TEXT trigger's label, the same dot a pill tab carries
         *  (PillTabItem.status). The dot is only a colour, so the label or `triggerTitle`
         *  must say what it means. Empty (default) draws no dot. */
        this.triggerStatus = '';
        this.triggerSize = 20;
        this.items = [];
        this.value = '';
        this.align = 'right';
        /** Render `<slot name="trigger">` inside the trigger button instead of an icon.
         *  For a trigger that is not a glyph — the account menu's avatar. Opt-in rather
         *  than slot-detection so the icon path stays the default for every existing
         *  caller. */
        this.slotTrigger = false;
        this.open = false;
        this.onDocClick = (e) => {
            if (this.open && !e.composedPath().includes(this))
                this.open = false;
        };
        this.onKey = (e) => {
            if (e.key === 'Escape')
                this.open = false;
        };
    }
    connectedCallback() {
        super.connectedCallback();
        document.addEventListener('click', this.onDocClick);
        document.addEventListener('keydown', this.onKey);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('click', this.onDocClick);
        document.removeEventListener('keydown', this.onKey);
    }
    pick(value) {
        this.open = false;
        this.dispatchEvent(new CustomEvent('dropdown-select', { detail: { value }, bubbles: true, composed: true }));
    }
    static { this.styles = [
        iconButton,
        css `
      :host {
        position: relative;
        display: inline-flex;
      }
      .trigger {
        width: 36px;
        height: 36px;
      }
      .pill {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        height: var(--dropdown-height, 28px);
        padding: 0 var(--dropdown-pad-x, 10px);
        font: inherit;
        font-size: var(--dropdown-font, 12px);
        color: var(--text-primary);
        background: transparent;
        border: 1px solid var(--border-color);
        /* Pill by default; a caller can square it to the base-control radius (6px) by setting
           --dropdown-radius, e.g. the header's App/AI/Fleet switcher. */
        border-radius: var(--dropdown-radius, 999px);
        cursor: pointer;
        max-width: 100%;
        min-width: 0;
      }
      /* The LABEL truncates, never the chevron: a trigger that drops its affordance
         to fit stops reading as something you can open. */
      .pill > span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .pill > vitesse-icon {
        flex-shrink: 0;
      }
      .pill:hover {
        border-color: var(--blue);
        color: var(--blue);
      }
      .pill vitesse-icon {
        opacity: 0.7;
      }
      /* The pill tab's status dot (_base-pill-tab-bar.scss), drawn here because that
         stylesheet does not reach into this shadow root. */
      .dot {
        flex: none;
        width: 7px;
        height: 7px;
        border-radius: 50%;
      }
      .dot-green {
        background: var(--green);
      }
      .dot-yellow {
        background: var(--yellow);
      }
      .dot-red {
        background: var(--red);
      }
      .dot-gray {
        background: var(--gray);
      }
      .panel {
        position: absolute;
        top: calc(100% + 8px);
        min-width: 160px;
        background: var(--card-bg);
        border: 1px solid var(--border-color);
        border-radius: 10px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
        z-index: 200;
        overflow: hidden;
        padding: 4px 0;
        animation: in 0.15s ease-out;
      }
      .panel.right {
        right: 0;
      }
      .panel.left {
        left: 0;
      }
      @keyframes in {
        from {
          opacity: 0;
          transform: translateY(-4px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      .item {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        padding: 8px 12px;
        border: none;
        background: transparent;
        cursor: pointer;
        font-size: 13px;
        color: var(--text-secondary);
      }
      .item:hover {
        background: var(--hover-bg);
        color: var(--text-primary);
      }
      .item.active {
        color: var(--blue);
      }
      /* Destructive intent, same red the icon-button spec uses for turn-off. */
      .item.danger:hover {
        color: var(--red, #e03e42);
      }
      .item.divided {
        border-top: 1px solid var(--border-color);
        margin-top: 4px;
        padding-top: 8px;
      }
      /* The header slot renders NOTHING when unfilled — an empty slot has no box,
         so no caller pays a pixel for it. */
      .panel-header::slotted(*) {
        display: block;
        padding: 10px 12px;
        border-bottom: 1px solid var(--border-color);
        margin-bottom: 4px;
      }
      .label {
        flex: 1;
        text-align: left;
      }
      .swatch {
        width: 14px;
        height: 14px;
        border-radius: 4px;
        flex-shrink: 0;
      }
    `,
    ]; }
    render() {
        return html `
      ${this.triggerLabel
            ? html `<button class="pill" title=${this.triggerTitle} @click=${() => (this.open = !this.open)}>
            ${this.triggerIcon ? html `<vitesse-icon name=${this.triggerIcon} size="13"></vitesse-icon>` : nothing}
            <span>${this.triggerLabel}</span>
            ${this.triggerStatus ? html `<span class="dot dot-${this.triggerStatus}" aria-hidden="true"></span>` : nothing}
            <vitesse-icon name="chevron-down" size="12"></vitesse-icon>
          </button>`
            : html `<button class="icon-btn trigger" title=${this.triggerTitle} @click=${() => (this.open = !this.open)}>
            ${this.slotTrigger
                ? html `<slot name="trigger"></slot>`
                : html `<vitesse-icon name=${this.triggerIcon} size=${this.triggerSize}></vitesse-icon>`}
          </button>`}
      ${this.open
            ? html `<div class="panel ${this.align}">
            <slot class="panel-header" name="header"></slot>
            ${this.items.map((it) => html `
                <button
                  class="item ${it.value === this.value ? 'active' : ''} ${it.danger ? 'danger' : ''} ${it.divider ? 'divided' : ''}"
                  @click=${() => this.pick(it.value)}
                >
                  ${it.swatch
                ? html `<span class="swatch" style="background:${it.swatch}"></span>`
                : it.icon
                    ? html `<vitesse-icon name=${it.icon} size="14"></vitesse-icon>`
                    : nothing}
                  <span class="label">${it.label}</span>
                  ${it.value === this.value ? html `<vitesse-icon name="check" size="14"></vitesse-icon>` : nothing}
                </button>
              `)}
          </div>`
            : nothing}
    `;
    }
};
__decorate([
    property()
], VitesseDropdown.prototype, "triggerIcon", void 0);
__decorate([
    property()
], VitesseDropdown.prototype, "triggerLabel", void 0);
__decorate([
    property()
], VitesseDropdown.prototype, "triggerTitle", void 0);
__decorate([
    property()
], VitesseDropdown.prototype, "triggerStatus", void 0);
__decorate([
    property({ type: Number })
], VitesseDropdown.prototype, "triggerSize", void 0);
__decorate([
    property({ attribute: false })
], VitesseDropdown.prototype, "items", void 0);
__decorate([
    property()
], VitesseDropdown.prototype, "value", void 0);
__decorate([
    property()
], VitesseDropdown.prototype, "align", void 0);
__decorate([
    property({ type: Boolean })
], VitesseDropdown.prototype, "slotTrigger", void 0);
__decorate([
    state()
], VitesseDropdown.prototype, "open", void 0);
VitesseDropdown = __decorate([
    customElement('vitesse-dropdown')
], VitesseDropdown);
export { VitesseDropdown };
//# sourceMappingURL=vitesse-dropdown.js.map